// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Builds a LIXI premium package from the same options as the flat
 * Premium Quote wizard. Attribute names follow lixi-pq-request-01.json
 * and QuotationRequestMapper. Proposal-only fields are not emitted.
 * Instruction used: scope-discipline.instructions.md
 */

import { DEFAULT_WIZARD_OPTIONS } from './premium-quote-payload'
import { toPositiveInt, toPositiveNumber, toBool } from './premium-quote-payload'

// Inverse of lixi-premium-quotation-service PremiumQuoteAPIHelperService.purposeCodeMap.
const ABS_BY_PURPOSE = {
  PCH: 'ABS-125',
  REF: 'ABS-133',
  DCO: 'ABS-239',
  ERL: 'ABS-329',
  CFD: 'ABS-221',
  BRG: 'ABS-231',
  BCN: 'ABS-121',
  HIM: 'ABS-143',
  IRF: 'ABS-248'
}

const RESIDENTIAL_BY_SECURITY = {
  HO: 'Fully Detached House',
  UN: 'Apartment Unit Flat',
  CA: 'Apartment Unit Flat',
  SA: 'Bedsitter Bachelor',
  SM: 'Holiday Home',
  CO: 'Converted Commercial Property',
  CL: 'Converted Commercial Property',
  IN: 'Converted Industrial Property',
  VM: 'Vacant Land',
  VO: 'Vacant Land',
  GH: 'Fully Detached House',
  GU: 'Apartment Unit Flat',
  OT: 'Fully Detached House'
}

export function generateLixiPremiumPackage(options) {
  const opts = Object.assign({}, DEFAULT_WIZARD_OPTIONS, options || {})
  const errors = []
  const funderId = toPositiveInt(opts.funderId)
  if (funderId == null) {
    errors.push('funderId is required')
  }

  const requestId = lixiId(opts.requestId || ('portal-' + Date.now()))
  const state = opts.state || DEFAULT_WIZARD_OPTIONS.state
  const investment = (opts.occupancyType || DEFAULT_WIZARD_OPTIONS.occupancyType) === 'Investment'
  const loanCount = Number(opts.loanCount) === 2 ? 2 : 1
  const capitalised = toBool(opts.premiumToBeCapitalised, true)
  const purpose = opts.loanPurpose || DEFAULT_WIZARD_OPTIONS.loanPurpose
  const amount = toPositiveNumber(opts.loanAmount, DEFAULT_WIZARD_OPTIONS.loanAmount)
  const months = toPositiveInt(opts.loanTermInMonths) || DEFAULT_WIZARD_OPTIONS.loanTermInMonths
  const years = Math.max(1, Math.round(months / 12))
  const securityValue = toPositiveNumber(opts.securityValue, DEFAULT_WIZARD_OPTIONS.securityValue)

  const loans = []
  for (let i = 0; i < loanCount; i++) {
    loans.push({
      '@AmountRequested': amount,
      '@SequenceNumber': i + 1,
      '@UniqueID': 'LOAN-ID-' + (i + 1),
      '@LoanType': 'Amortising Home Loan',
      LendingPurpose: [{
        '@ABSLendingPurposeCode': ABS_BY_PURPOSE[purpose] || ABS_BY_PURPOSE.PCH
      }],
      LoanPurpose: {
        '@PrimaryPurpose': investment ? 'Investment Residential' : 'Owner Occupied'
      },
      Security: [{ '@SequenceNumber': 1, '@x_Security': 'REA001' }],
      Term: {
        '@TotalTermDuration': years,
        '@TotalTermUnits': 'Years',
        '@PaymentType': 'Principal and Interest',
        '@InterestType': 'Variable'
      }
    })
  }

  const asset = {
    '@UniqueID': 'REA001',
    '@PrimaryPurpose': investment ? 'Investment' : 'Owner Occupied',
    '@Status': 'Established',
    Residential: {
      '@Type': RESIDENTIAL_BY_SECURITY[opts.securityType] || RESIDENTIAL_BY_SECURITY.HO
    },
    '@x_Address': 'ADD-SECURITY'
  }
  // QuotationRequestMapper reads ContractDetails for purchase, construction, and estimate.
  // Instruction used: debugging.instructions.md
  asset.ContractDetails = { '@ContractPriceAmount': securityValue }

  const application = {
    '@ProductionData': 'No',
    Address: [address('ADD-RESIDENTIAL', state), address('ADD-SECURITY', state)],
    BusinessChannel: {
      '@CompanyName': 'Portal',
      '@Type': 'Bank',
      '@UniqueID': 'BUSINESSCHANNEL-001'
    },
    Funder: [{
      '@CompanyName': 'Funder',
      '@OtherIdentifier': funderId != null ? String(funderId) : undefined
    }],
    Insurance: [{
      '@ProductName': opts.productName || DEFAULT_WIZARD_OPTIONS.productName,
      '@PremiumInsured': capitalised ? 'Yes' : 'No',
      // Quote deserialiser expects a number. "100" returns E_LIXI_DATATYPE_MISMATCH.
      // Instruction used: debugging.instructions.md
      '@PercentCoverRequired': 100,
      '@UniqueID': 'INS-DETAILS-001'
    }],
    LoanDetails: loans,
    Overview: {
      '@LenderApplicationReferenceNumber': requestId.slice(0, 20)
    },
    RealEstateAsset: [asset]
  }

  if (opts.borrowerType === 'Company') {
    application.CompanyApplicant = [{
      '@UniqueID': 'CA001',
      '@ApplicantType': 'Borrower',
      '@BusinessName': 'Sample Pty Ltd',
      '@ABN': '51000000001',
      '@PrimaryApplicant': 'Yes',
      '@TypeOfIncorporation': 'Pty Ltd'
    }]
  } else {
    application.PersonApplicant = [person('Borrower', 'PA001', '1985-06-15', 'Yes')]
  }
  if (toBool(opts.includeGuarantor, false)) {
    application.PersonApplicant = (application.PersonApplicant || []).concat([
      person('Guarantor', 'PA002', '1960-03-20', 'No', 'Guarantor')
    ])
  }

  return {
    payload: {
      Package: {
        '@ProductionData': 'No',
        '@UniqueID': requestId,
        Content: { Application: application },
        // G0MandatoryAndOthValidator rejects a package without these.
        // Instruction used: debugging.instructions.md
        Instructions: {
          LendersMortgageInsuranceInstructions: {
            Submit: {
              '@SubmissionType': 'Quote Request'
            }
          }
        },
        Publisher: { '@LIXICode': 'CUSTOMER_LIXI' },
        Recipient: [{ '@LIXICode': 'HELIA_LIXI' }],
        SchemaVersion: {
          '@LIXITransactionType': 'LMI',
          '@LIXIVersion': '0.1.31'
        }
      }
    },
    errors: errors
  }
}

function person(surname, id, dateOfBirth, primary, applicantType) {
  return {
    '@ApplicantType': applicantType || 'Borrower',
    '@DateOfBirth': dateOfBirth,
    '@PrimaryApplicant': primary,
    '@UniqueID': id,
    PersonName: {
      '@FirstName': 'Sample',
      '@Surname': surname
    }
  }
}

function address(id, state) {
  return {
    '@AustralianPostCode': '2000',
    '@AustralianState': state,
    '@Country': 'AU',
    '@Suburb': 'Sydney',
    '@Type': 'Standard',
    '@UniqueID': id,
    Standard: {
      '@StreetName': 'Sample',
      '@StreetNumber': '1',
      '@StreetType': 'Street'
    }
  }
}

function lixiId(value) {
  const cleaned = String(value).replace(/[^a-zA-Z0-9-_.]/g, '-')
  return /^[a-zA-Z_]/.test(cleaned) ? cleaned : ('Q-' + cleaned)
}
